import { useEffect, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, Paper, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import type { ProductTourPageId } from "./product-tour";
import { PRODUCT_TOUR_EVENT } from "./product-tour";

type Lesson = { title: string; tasks: string[]; next?: { path: string; label: string } };
export const PRACTICE_LESSONS: Record<ProductTourPageId, Lesson> = {
  home: {
    title: "Learn from a game, then test one habit",
    tasks: [
      "Pick one finished game you remember. Our worked example: before taking an undefended piece, check the opponent’s forcing reply. Your game may support a different lesson; follow its evidence.",
      "Import and analyze it, review one decision, inspect the position behind the explanation, then choose one next-game cue. Patterns can start this process too; reviewing a game first is optional.",
      "Play another game at the same pace on your usual chess service, import it, and compare the relevant decisions. One win or accuracy change does not prove improvement.",
    ],
    next: { path: "/analyze", label: "Start with a finished game" },
  },
  analyze: {
    title: "Import → analyze → open saved evidence",
    tasks: [
      "Choose Target game format and Target game clock, then choose IMPORT GAMES. Confirm your chess username in Profile if the library is empty. For a public Chess.com game link or PGN study, use Board → Import game instead; studies stay outside personal Games and Patterns.",
      "Choose Quick, Regular, or Deep. More calculation takes longer; start with a depth you can wait for. Check the queue’s READY count, then choose START ANALYSIS to analyze the selected scope.",
      "Watch the queue until saving completes. Keep this page open while it works. If analysis fails, read the error and retry; an imported game alone has no verified analysis.",
      "Open the completed game from analysis history. Check that its saved review contains move evidence before continuing. If no game is ready, return to this lesson after analysis finishes.",
    ],
  },
  review: {
    title: "Practice one decision in Guided, then explore Open",
    tasks: [
      "Choose a critical moment and Guided mode in Review. If there are no critical moments, use Open and select an analyzed move instead; the four Guided stages need a selected moment.",
      "Think: look at the board before revealing the answer. Enter Candidate moves and your assessment in the private note, then choose Save private note. Predict the opponent’s reply. Ask: what changed in this position? Choose Continue to Test; the board now accepts a temporary line while answers remain hidden.",
      "Test: play your candidate on the actual board, then a legal opponent reply. Explain what your line accomplishes. These temporary moves do not replace the recorded game. Continue to Verify when ready.",
      "Verify: compare your prediction with the saved evaluation and move evidence. Play the available verified continuation one move at a time. Point to the move that explains the consequence; do not infer a cause from a motif label alone.",
      "Carry: continue to Carry and save one usable cue in your review note. Example: ‘Before taking a loose piece, check forcing replies.’ Adapt it to the verified consequence in your game. Open Pattern evidence when offered.",
      "Switch to Open. Select a move in Moves or a point on the evaluation timeline. Check side to move, the board, evaluation perspective, and clocks at that exact move. Time spent and evaluation describe different things.",
      "Open Details. Expand a nonzero move-quality, motif, or principle count and select an occurrence. Confirm that the board jumps to its move. Move quality describes evaluation change; a motif names a tactical structure; a principle describes a positional idea. A zero count means no recorded occurrence. Use highlighted piece relationships to locate the structure, then verify its consequence with the supported line; a badge alone does not prove why a move failed.",
      "Play a verified continuation when available, then return to the recorded position. Try your own legal alternative on the board and reset it before selecting another move. Explain which line is recorded, which is verified, and which is your experiment.",
      "If Nikola is available, ask ‘Why does this reply change the position?’ Open any cited move and check the board yourself. If evidence is missing, keep the question open rather than treating an explanation as proof.",
    ],
    next: { path: "/patterns", label: "Look for the same decision across games" },
  },
  patterns: {
    title: "Turn repeated evidence into one practice cue",
    tasks: [
      "Choose one format, exact clock, and time window. If the sample is empty, import and analyze more games at that pace; do not combine formats to manufacture a pattern.",
      "Choose a skill or pattern. Read its affected-game count and open an evidence example. Identify what the player could have noticed before moving.",
      "Browse a second example when available. What decision repeats, and what differs? One occurrence is evidence, not a recurring habit; a count is not statistical confidence.",
      "Open the exact game and move to verify the consequence. Save a short cue in that game’s review note, or write it down for your next game. Choose one cue you can actually remember during play.",
      "Play at the same pace on your usual chess service, then import and analyze the new game. Revisit the same pattern and inspect whether the relevant decision changed, even if the result did not.",
    ],
    next: { path: "/trends", label: "Compare later games at the same pace" },
  },
  trends: {
    title: "Compare fairly after practice",
    tasks: [
      "Choose the same format and exact clock as the games used for your cue. Compare an earlier and later period with enough analyzed games to inspect.",
      "Hover or touch a chart point to read its value and date. Separate rating, accuracy, errors, activity, and repertoire: each answers a different question.",
      "Return to real positions behind the periods. Did you notice the cue and make a better decision when it applied? Results alone cannot tell you that.",
      "Keep, simplify, or replace the cue based on those positions. With sparse evidence, collect more games; forecasts and small changes are not guarantees of progress.",
    ],
    next: { path: "/analyze", label: "Import your next practice game" },
  },
  board: {
    title: "Explore safely on the Board",
    tasks: [
      "Choose Board mode, then Import game to open Details. Paste a completed public game into Chess.com game link and choose Import public game. Alternatively use Choose PGN file, or paste PGN text and choose Load PGN. Confirm players, selected game, and side to move. This is a study: it does not enter personal Games or Patterns.",
      "Open Moves to navigate the imported game. Watch Regular full-game review progress and wait for completion before interpreting move-quality labels; use Retry if it fails. In Review, Analysis depth → Regular · Depth 16 controls the current-position analysis; this is different from analyzing every recorded move.",
      "Play a candidate and an opponent reply. Navigate backward and forward to explain what changed. Temporary exploration is separate from saved game history.",
      "Inspect verified analysis, motif and principle badges, and highlighted piece relationships. Play the supported continuation when present. Compare its consequence with your prediction; an unverified line is still an experiment. Wait until analysis completes instead of treating a provisional score as final.",
      "When Nikola is available, select Ask Nikola about this move. The selected move is marked as the question’s context. Ask why its verified reply matters; return to the board and inspect the cited consequence before accepting the explanation.",
      "Use Reset board when you want a fresh position. For progress tracking, play on your usual chess service, then import and analyze that finished game.",
    ],
    next: { path: "/analyze", label: "Analyze a finished practice game" },
  },
  games: {
    title: "Find a decision worth reviewing",
    tasks: [
      "Choose a format, then a clock group. Read the selected scope before comparing game results.",
      "Open an analyzed game and find one decision you can explain. Send a game without evidence to Analyze first.",
    ],
    next: { path: "/analyze", label: "Analyze games without evidence" },
  },
  archive: {
    title: "Prepare your game library",
    tasks: [
      "Filter by format, clock, and analysis state. Identify one finished game you want to study.",
      "Select the game for analysis or open its saved review. Library deletion is not part of this exercise.",
    ],
    next: { path: "/analyze", label: "Continue to analysis" },
  },
  chats: {
    title: "Ask a question you can verify",
    tasks: [
      "Open a conversation or start one when Nikola is available. Name a game, move, or same-format question.",
      "Ask for the evidence behind the answer. Follow a game reference and explain the consequence on the board yourself.",
    ],
  },
  chat: {
    title: "Use Nikola to explain evidence",
    tasks: [
      "Ask one concrete question, such as ‘What reply did I miss in this position?’ Include a game and move when needed.",
      "Follow an available reference and compare the answer with the exact board. Ask for a simpler explanation if you cannot explain the consequence yourself.",
      "Turn the supported finding into one next-game cue. Missing evidence means the conclusion remains open.",
    ],
    next: { path: "/patterns", label: "Check whether it repeats" },
  },
  settings: {
    title: "Set up a comfortable study session",
    tasks: [
      "Choose a board and appearance you can read clearly, then inspect analysis and notification preferences.",
      "Review Nikola privacy controls before providing personal context. Your stated goals guide coaching; they are not verified findings about your play.",
    ],
  },
  profile: {
    title: "Check your chess identity",
    tasks: [
      "Confirm your chess username and account details so imported games represent the intended player.",
      "Open Settings for coaching context and privacy, or Membership for access and credits. No account or billing change is required for this lesson.",
    ],
  },
  membership: {
    title: "Understand access before buying",
    tasks: [
      "Read current access and credit balance. Match any limit you encounter to the feature you want to use.",
      "Compare available options only if you need more access. Completing a tutorial never requires a purchase.",
    ],
  },
  help: {
    title: "Replay the learning loop",
    tasks: [
      "Open the page you want to practice and replay its tutorial. Work through each task beside the real controls; Show me locates a control, and Show page controls opens the optional visual overview.",
      "Return to one finished game: analyze, review a decision, verify evidence, choose a cue, play, and compare later games at the same pace.",
    ],
    next: { path: "/analyze", label: "Begin the learning loop" },
  },
  about: {
    title: "What makes a useful lesson?",
    tasks: [
      "Start with a real position and verified consequence. Use Game Review for one decision or Patterns for repeated decisions; neither entry point requires the other.",
      "Nikola explains supported facts. Test your understanding on the board, carry one cue into play, and revisit later evidence.",
    ],
    next: { path: "/home", label: "Choose your next step" },
  },
};

export function practiceTarget(page: ProductTourPageId, step: number): string | null {
  if (page === "review")
    return step === 0
      ? '[aria-label="Review mode"]'
      : step < 5
        ? '[data-tour="review-review-panel"]'
        : step === 6
          ? '[data-tour="review-details-panel"]'
          : step === 8
            ? '[data-tour="review-nikola-panel"]'
            : '[data-testid="game-review-board-rail"]';
  if (page === "board")
    return step === 0
      ? '[data-testid="board-sidebar"]'
      : step === 1
        ? '[data-testid="board-move-history"]'
        : step === 4
          ? '[data-testid="board-sidebar"]'
          : '[data-testid="board-position"]';
  if (page === "analyze")
    return step === 0
      ? '[aria-label="Target game format"]'
      : step === 1
        ? '[data-tour="analysis-presets"]'
        : step === 2
          ? '[data-tour="analysis-queue"]'
          : '[data-tour="analysis-history"]';
  // Patterns' explorer list is on screen at every width; its evidence panel is not on phones until a row is chosen.
  if (page === "patterns") return step === 0 ? '[data-tour="patterns-format"]' : '[data-tour="explorer-list"]';
  if (page === "trends") return step === 0 ? '[data-tour="trends-format"]' : "figure";
  return null;
}

type PracticeProgress = { checked: string[]; steps: Partial<Record<ProductTourPageId, number>> };
export function readPracticeProgress(key: string): PracticeProgress {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(key) ?? "null");
    if (
      value &&
      typeof value === "object" &&
      "checked" in value &&
      "steps" in value &&
      Array.isArray(value.checked) &&
      value.checked.every((item: unknown) => typeof item === "string") &&
      value.steps &&
      typeof value.steps === "object"
    )
      return { checked: value.checked, steps: value.steps };
  } catch {
    /* Storage may be unavailable. Practice remains usable. */
  }
  return { checked: [], steps: {} };
}

export function TutorialPractice({
  page,
  userId,
  onClose,
}: {
  page: ProductTourPageId;
  userId: string;
  onClose: () => void;
}) {
  const storageKey = `chessla:practice:${userId}:v1`;
  const [collapsed, setCollapsed] = useState(false);
  const [progress, setProgress] = useState(() => readPracticeProgress(storageKey));
  const [targetMessage, setTargetMessage] = useState("");
  const lesson = PRACTICE_LESSONS[page];
  const rawStep = progress.steps[page];
  const step =
    typeof rawStep === "number" && Number.isInteger(rawStep)
      ? Math.max(0, Math.min(rawStep, lesson.tasks.length - 1))
      : 0;
  const id = `${page}:${step}`;
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(progress));
    } catch {
      /* Optional persistence. */
    }
  }, [progress, storageKey]);
  useEffect(() => {
    setCollapsed(false);
    setTargetMessage("");
  }, [page]);
  const move = (next: number) => {
    setTargetMessage("");
    setProgress((value) => ({ ...value, steps: { ...value.steps, [page]: next } }));
  };
  const showTarget = () => {
    const selector = practiceTarget(page, step);
    const visible = (element: HTMLElement) =>
      element.getClientRects().length > 0 &&
      !element.closest('[hidden], [aria-hidden="true"]') &&
      getComputedStyle(element).visibility !== "hidden";
    const target = selector ? Array.from(document.querySelectorAll<HTMLElement>(selector)).find(visible) : null;
    if (!target) {
      setTargetMessage(
        "Open the tab or finish the prerequisite described above, then try Show me again. This control is not currently visible.",
      );
      return;
    }
    target.scrollIntoView({ behavior: "auto", block: "center" });
    const controls =
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';
    const focusable = target.matches(controls)
      ? target
      : Array.from(target.querySelectorAll<HTMLElement>(controls)).find(visible);
    if (focusable) focusable.focus({ preventScroll: true });
    else {
      const previous = target.getAttribute("tabindex");
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
      target.addEventListener(
        "blur",
        () => {
          if (previous === null) target.removeAttribute("tabindex");
          else target.setAttribute("tabindex", previous);
        },
        { once: true },
      );
    }
    setCollapsed(true);
  };
  return (
    <Paper
      component="aside"
      aria-label="Practice tutorial"
      elevation={8}
      sx={{
        position: "fixed",
        right: 12,
        bottom: 12,
        zIndex: 1200,
        width: "min(390px, calc(100vw - 24px))",
        maxHeight: "55dvh",
        overflowY: "auto",
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          position: "sticky",
          top: -16,
          zIndex: 1,
          bgcolor: "background.paper",
          mx: -2,
          mt: -2,
          px: 2,
          py: 1,
        }}
      >
        <Button
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-controls="practice-tutorial-content"
        >
          {collapsed ? "Resume tutorial" : "Collapse tutorial"}
        </Button>
        <Button onClick={onClose} aria-label="Close practice tutorial">
          Close
        </Button>
      </Box>
      {!collapsed && (
        <Box id="practice-tutorial-content">
          <Typography component="h2" variant="h6">
            {lesson.title}
          </Typography>
          <Button
            onClick={() => window.dispatchEvent(new CustomEvent(PRODUCT_TOUR_EVENT, { detail: { mode: "controls" } }))}
          >
            Show page controls
          </Button>
          <Typography variant="body2" sx={{ my: 1 }}>
            Use the real page controls. Check each task after trying it; checks record your reflection, not a verified
            skill score. Collapse this panel whenever it covers a control.
          </Typography>
          <Typography role="status" variant="overline">
            Step {step + 1} of {lesson.tasks.length}
          </Typography>
          <Typography sx={{ my: 1 }}>{lesson.tasks[step]}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={progress.checked.includes(id)}
                onChange={(_, value) =>
                  setProgress((current) => ({
                    ...current,
                    checked: value
                      ? [...new Set([...current.checked, id])]
                      : current.checked.filter((item) => item !== id),
                  }))
                }
              />
            }
            label="I tried this and can explain what happened"
          />
          {practiceTarget(page, step) && <Button onClick={showTarget}>Show me</Button>}
          {targetMessage && (
            <Typography role="status" variant="body2">
              {targetMessage}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button disabled={step === 0} onClick={() => move(step - 1)}>
              Previous task
            </Button>
            <Button disabled={step === lesson.tasks.length - 1} onClick={() => move(step + 1)}>
              Next task
            </Button>
          </Box>
          {step === lesson.tasks.length - 1 && lesson.next && (
            <Button component={Link} to={lesson.next.path} sx={{ mt: 1 }}>
              {lesson.next.label}
            </Button>
          )}
          <Button
            onClick={() => {
              setProgress((current) => ({
                checked: current.checked.filter((item) => !item.startsWith(`${page}:`)),
                steps: { ...current.steps, [page]: 0 },
              }));
              setTargetMessage("");
            }}
          >
            Restart this lesson
          </Button>
        </Box>
      )}
    </Paper>
  );
}
